
import React, { useState } from 'react';

interface FinancialStepProps {
  t: any;
  isRTL: boolean;
  language: string;
  onBack: () => void;
  onHome: () => void;
  onContinue: (fin: { planType: 'buy' | 'rent', range: string }) => void;
}

const FinancialStep: React.FC<FinancialStepProps> = ({ t, isRTL, language, onBack, onHome, onContinue }) => {
  const [planType, setPlanType] = useState<'buy' | 'rent'>('buy');
  const [selectedRange, setSelectedRange] = useState<string>('');

  const buyRanges = [
    { id: 'value', label: t.valueFocus, price: '< $450K', icon: 'savings' },
    { id: 'balanced', label: t.balanced, price: '$450K - $800K', icon: 'account_balance' },
    { id: 'prime', label: t.primeHubs, price: '$800K - $1.5M', icon: 'apartment' },
    { id: 'luxury', label: t.luxury, price: '$1.5M +', icon: 'diamond' },
  ];

  const rentRanges = [
    { id: 'value', label: t.valueFocus, price: '< $2.2K', icon: 'savings' },
    { id: 'balanced', label: t.balanced, price: '$2.2K - $4.5K', icon: 'vpn_key' },
    { id: 'prime', label: t.primeHubs, price: '$4.5K - $8K', icon: 'domain' },
    { id: 'luxury', label: t.luxury, price: '$8K +', icon: 'auto_awesome' },
  ];

  const ranges = planType === 'buy' ? buyRanges : rentRanges;
  const infoItems = planType === 'buy' ? t.buyInfo : t.rentInfo;

  const handlePlanToggle = (type: 'buy' | 'rent') => {
    setPlanType(type);
    setSelectedRange(''); 
  };

  return (
    <div className={`relative flex flex-col h-screen w-full max-w-2xl mx-auto font-display ${isRTL ? 'text-right' : 'text-left'}`}>
      <header className={`relative w-full pt-6 md:pt-10 px-8 md:px-12 pb-2 md:pb-4 flex items-center justify-between z-20`}>
        <button 
          onClick={onHome}
          className={`flex items-center gap-2.5 transition-opacity hover:opacity-80 active:scale-95 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
        >
          <span className="material-symbols-outlined text-[#0066FF] text-[26px] md:text-[30px] font-bold">flight_takeoff</span>
          <h2 className="text-slate-dark tracking-tighter text-[20px] md:text-[22px] font-black leading-none">Settlo</h2>
        </button>
        <button 
          onClick={onBack}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100 text-slate-soft hover:text-primary transition-all active:scale-95 group"
        >
          <span className="text-[12px] font-bold uppercase tracking-[0.1em]">{language === 'zh' ? '返回' : 'Back'}</span>
          <span className={`material-symbols-outlined text-[18px] ${isRTL ? '' : 'rotate-180'}`}>arrow_forward</span>
        </button>
      </header>

      <main className="px-8 md:px-12 pb-2 flex flex-col flex-grow pt-4 md:pt-8 overflow-y-auto no-scrollbar">
        <div className={`mb-6 md:mb-8 flex flex-col ${isRTL ? 'items-end' : 'items-start'}`}>
          <div className="text-slate-soft/60 text-[11px] font-black tracking-[0.3em] uppercase mb-3">
            {t.step}
          </div>
          <h1 className="text-[28px] md:text-[42px] font-extrabold tracking-tight leading-[1.1] text-slate-dark mb-4">
            {t.title}
          </h1>
        </div>

        {/* Tab Toggle - Mimicking Premium UI */}
        <div className="w-full mb-8">
          <div className="inline-flex p-1.5 bg-slate-100 rounded-stripe w-full">
            <button 
              onClick={() => handlePlanToggle('buy')} 
              className={`flex-1 px-6 py-2.5 rounded-[10px] font-bold text-[14px] transition-all duration-300 ${planType === 'buy' ? 'bg-white text-[#0066FF] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              {t.buy}
            </button>
            <button 
              onClick={() => handlePlanToggle('rent')} 
              className={`flex-1 px-6 py-2.5 rounded-[10px] font-bold text-[14px] transition-all duration-300 ${planType === 'rent' ? 'bg-white text-[#0066FF] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              {t.rent}
            </button>
          </div>
        </div>

        <section className="flex-grow">
          <div className="flex flex-col gap-3 md:gap-4 mb-10">
            {ranges.map((range) => (
              <button
                key={range.id}
                onClick={() => setSelectedRange(range.id)}
                className={`group relative flex items-center gap-4 md:gap-6 p-4 md:p-5 rounded-stripe border transition-all duration-300 transform active:scale-[0.99] ${
                  selectedRange === range.id 
                    ? 'bg-primary/5 border-primary shadow-lg shadow-primary/5' 
                    : 'bg-white border-[#E6E6E8] shadow-stripe-card hover:border-primary/40'
                } ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}
              >
                {/* Icon Box: Aligned with Choice Hub Style */}
                <div className={`flex-none w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                  selectedRange === range.id ? 'bg-primary text-white scale-105' : 'bg-slate-50 text-slate-soft group-hover:bg-primary/10'
                }`}>
                  <span className="material-symbols-outlined text-[26px] md:text-[30px]" style={{ fontVariantLigatures: 'none' }}>
                    {range.icon}
                  </span>
                </div>

                {/* Text Block: Bold Title, Medium Price (The Key fix) */}
                <div className="flex-1 min-w-0">
                  <div className={`text-[17px] md:text-[19px] font-bold tracking-tight mb-1 transition-colors duration-300 ${
                    selectedRange === range.id ? 'text-primary' : 'text-slate-dark group-hover:text-primary'
                  }`}>
                    {range.label}
                  </div>
                  <div className={`text-[14px] md:text-[15px] font-medium transition-colors duration-300 ${
                    selectedRange === range.id ? 'text-primary/70' : 'text-slate-soft'
                  }`}>
                    {range.price}
                  </div>
                </div>

                {/* Status Indicator */}
                <div className={`flex-none transition-all duration-500 ${
                  selectedRange === range.id ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-50 rotate-90'
                }`}>
                  <span className="material-symbols-outlined text-primary text-[28px] font-bold">check_circle</span>
                </div>
              </button>
            ))}
          </div>

          {/* Info Card - Consistent with overall aesthetics */}
          <div className={`p-5 md:p-6 bg-slate-50/80 rounded-stripe border border-slate-100 ${isRTL ? 'text-right' : 'text-left'}`}>
            <h3 className="text-slate-soft/50 text-[10px] md:text-[11px] font-black uppercase tracking-[0.25em] mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-[16px]">analytics</span>
              {t.whyTitle}
            </h3>
            <div className="grid grid-cols-1 gap-y-3.5">
              {infoItems.map((item: any, idx: number) => (
                <div key={idx} className="flex items-center justify-between gap-4 border-b border-slate-200/30 pb-2.5 last:border-0 last:pb-0">
                  <span className="text-[13px] md:text-[14px] font-bold text-slate-dark tracking-tight">{item.label}</span>
                  <span className="text-[12px] md:text-[13px] text-slate-soft font-medium opacity-80">{item.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="w-full px-8 md:px-12 pb-8 md:pb-12 pt-4 shrink-0">
        <button 
          disabled={!selectedRange}
          onClick={() => selectedRange && onContinue({ planType, range: selectedRange })}
          className={`group w-full relative flex items-center justify-center gap-2.5 font-black text-[17px] py-4 md:py-5 rounded-stripe shadow-stripe-button transition-all duration-300 active:scale-[0.99] ${
            selectedRange ? 'bg-[#0066FF] text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >
          <span>{t.continue}</span>
        </button>
      </footer>
    </div>
  );
};

export default FinancialStep;
